import { useMemo } from "react";
import { batchMasterApi } from "../../../api/batchMasterApi";
import { useApiQuery } from "../../../hooks/useApiQuery";

export default function useBatchFormOptions(open, plantId, suppliedParts, suppliedMaterials, rawMaterialId) {
  const needsParts = !suppliedParts?.length;
  const needsMaterials = !suppliedMaterials?.length;
  const { data, error, isFetching, refetch } = useApiQuery(
    async () => {
      const [items, materials] = await Promise.all([
        needsParts ? batchMasterApi.items(plantId) : [],
        needsMaterials ? batchMasterApi.materials(plantId) : [],
      ]);
      return { plantId, items, materials };
    },
    [open, plantId, needsParts, needsMaterials],
    { skip: !open || !plantId || (!needsParts && !needsMaterials) }
  );
  const partOptions = useMemo(() => suppliedParts?.length ? suppliedParts : (
    data && data.plantId === plantId ? data.items ?? [] : []
  ).map((item) => ({
    value: item.item_id,
    label: [item.item_name, item.item_code, item.item_id].filter(Boolean).join(" · "),
    production_cycle_time: item.production_cycle_time,
    raw: item,
  })), [data, plantId, suppliedParts]);
  const materialOptions = useMemo(() => suppliedMaterials?.length ? suppliedMaterials : (
    data && data.plantId === plantId ? data.materials ?? [] : []
  ).map((material) => ({
    value: material.raw_material_id,
    raw_material_code: material.raw_material_code,
    label: [material.raw_material_name, material.raw_material_code, material.raw_material_id].filter(Boolean).join(" · "),
  })), [data, plantId, suppliedMaterials]);
  const inputs = useApiQuery(
    async () => ({ plantId, rawMaterialId, rows: await batchMasterApi.inputBatches(plantId, rawMaterialId) }),
    [open, plantId, rawMaterialId],
    { skip: !open || !plantId || !rawMaterialId }
  );
  const inputRecords = inputs.data?.plantId === plantId && inputs.data?.rawMaterialId === rawMaterialId
    ? inputs.data.rows : [];
  return {
    partOptions, materialOptions, inputRecords,
    error: error || inputs.error,
    isLoading: isFetching,
    isLoadingInputs: inputs.isFetching,
    refetch: () => { refetch(); inputs.refetch(); },
  };
}
